import React, {useEffect, useMemo} from "react";
import {Alert, Box} from "@mui/material";
import {
    INeighbourhood,
    Neighbourhood,
    SeatingPlanNewNeighbourhood,
    SeatingPlanNewNeighbourhoodForm,
    UNASSIGNED_NEIGHBOURHOOD_ID,
} from "../../api/Model";
import {useSeatingPlanStore} from "../../api/SeatingPlanStore";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {NewNeighbourhoodFormComponent} from "./NewNeighbourhoodFormComponent";
import {
    NeighbourhoodCard,
    NeighbourhoodPaneEmployeeBlock,
    NeighbourhoodPaneStatBlock,
    NeighbourhoodPaneVisitChartBlock
} from "../../components/neighbourhood-pane/NeighbourhoodCard";
import {SeatingPlanModel} from "./SeatingPlanModel";
import {useFilter} from "../../util/FilterSerializer";
import {filterHoodItems} from "./NeighbourhoodListFilter";
import {
    NeighbourhoodListFilter,
    neighbourhoodListFilterSerializer
} from "../neighbourhood-list/NeighbourhoodListFilter";
import {downloadNeighbourhoodEmployeesCsv, downloadNeighbourhoodsCsv} from "./ExportNeighbourhoodsCsv";
import {PageActionHandler} from "../../components/page-action/PageActionHandler";
import {newNeighbourhoodAction} from "./actions/CreateNewSeatingPlanNeighbourhoodMenuItem";
import {SeatingPlanTab} from "./SeatingPlanTab";
import {exportCsvMenuItem} from "./actions/SeatingPlanExportMenuItem";
import {usePermission} from "../../util/access/UseAccess";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {useMessage} from "../../util/actions/Message";
import {PRINT_TARGET} from "../../util/PrintUtil";
import {Virtuoso} from "react-virtuoso";
import {SeatingPlanStickyFilter} from "./SeatingPlanStickyFilter";
import {SeatingPlanTabLabel} from "./SeatingPlanTabLabel";
import {VisitDataCoverageWarning} from "../employee-visit-report/VisitDataCoverageWarning";
import {applyFilter, filterDateRange} from "../../util/FilterUtil";

function NeighbourhoodListTab({model, actionHandler}: {
    model: SeatingPlanModel,
    actionHandler: PageActionHandler<'exportNeighbourhoods' | 'exportNeighbourhoodEmployees' | 'newNeighbourhoodAction'>,
}) {
    const plan = model.plan;
    const message = useMessage();
    const {canAdmin} = usePermission({canAdmin: SeatingPlans_Admin});
    const {
        updateNewNeighbourhood,
        deleteNewNeighbourhood,
        updateNeighbourhoodUpdate,
        deleteNeighbourhoodUpdate,
    } = useSeatingPlanStore();

    const [filter, setFilter] = useFilter(neighbourhoodListFilterSerializer);


    const handleNewEdit = useFormDialogAction({
        title: 'Edit Neighbourhood',
        description: 'Update the neighbourhood information below.',
        submitButtonText: 'Update',
        renderForm: NewNeighbourhoodFormComponent,
        action: (openDialog, newNeighbourhood: SeatingPlanNewNeighbourhood) => openDialog({
            initialValue: {
                name: newNeighbourhood.name,
                displayColor: newNeighbourhood.displayColor || '',
                notes: newNeighbourhood.notes || null,
                slackChannelId: newNeighbourhood.slackChannelId || null,
            },
            onSubmit: (form) => updateNewNeighbourhood(plan, newNeighbourhood, form),
            successMessage: form => `Updated neighbourhood ${form.name}.`,
            errorMessage: 'Failed to update neighbourhood. Please try again later.',
        }),
    }, [updateNewNeighbourhood, plan]);

    function isSameNF(a: SeatingPlanNewNeighbourhoodForm, b: SeatingPlanNewNeighbourhoodForm) {
        return a.name === b.name && (a.displayColor || '') === (b.displayColor || '') && (a.notes || '') === (b.notes || '') && (a.slackChannelId || '') === (b.slackChannelId || '');
    }

    const handleExistingEdit = useFormDialogAction({
        title: 'Edit Neighbourhood',
        description: 'Update the neighbourhood information below.',
        submitButtonText: 'Update',
        renderForm: NewNeighbourhoodFormComponent,
        action: (openDialog, neighbourhood: Neighbourhood & {
            originalProps?: SeatingPlanNewNeighbourhoodForm
        }) => openDialog({
            initialValue: {
                name: neighbourhood.name,
                displayColor: neighbourhood.displayColor || '',
                notes: neighbourhood.notes || '',
                slackChannelId: neighbourhood.slackChannelId || '',
            },
            onSubmit: (form) => {
                if (neighbourhood.originalProps && isSameNF(form, neighbourhood.originalProps)) {
                    return deleteNeighbourhoodUpdate(plan, neighbourhood);
                } else {
                    return updateNeighbourhoodUpdate(plan, neighbourhood, form);
                }
            },
            successMessage: form => `Updated neighbourhood ${form.name}.`,
            errorMessage: 'Failed to update neighbourhood. Please try again later.',
        }),
    }, [updateNeighbourhoodUpdate, deleteNeighbourhoodUpdate, plan]);

    const handleDelete = useDeleteAction({
        description: 'Are you sure you want to delete this new neighbourhood? This action cannot be undone.',
        action: async (newNeighbourhood: SeatingPlanNewNeighbourhood) => deleteNewNeighbourhood(plan, newNeighbourhood),
        successMessage: 'Deleted neighbourhood',
        failureMessage: 'Failed to delete neighbourhood. Please try again later.',
    }, [deleteNewNeighbourhood, plan]);

    const {filteredHoods, statRanges} = useMemo(
        () => {
            const {filteredHoods, statRanges} = filterHoodItems(model.employeeVisits, model.assignedDesks, model.hoodItems, filter);
            return {
                filteredHoods: filteredHoods
                    .map(hood => ({
                        key: hood.key,
                        employees: hood.newEmployees,
                        stats: hood.stats,
                        neighbourhood: hood,
                        ...(hood.type === 'new' ? {
                            highlight: true,
                            onEdit: canAdmin ? handleNewEdit as (hood: INeighbourhood) => void : undefined,
                            onDelete: canAdmin ? handleDelete as (hood: INeighbourhood) => void : undefined,
                        } : {
                            highlight: hood.originalProps !== undefined || model.isMoved({hood})
                                || hood.newDesks.some(desk => model.isDeskModified(desk)),
                            onEdit: canAdmin && hood.id !== UNASSIGNED_NEIGHBOURHOOD_ID
                                ? handleExistingEdit as (hood: INeighbourhood) => void
                                : undefined,
                            onDelete: undefined,
                        })
                    })),
                statRanges,
            };
        },
        [model, filter, handleNewEdit, handleExistingEdit, handleDelete, canAdmin]
    );

    useEffect(() => actionHandler.setHandler('exportNeighbourhoods', () => {
        if (filteredHoods.length <= 0) {
            message.error('No neighbourhoods found to export.');
        } else if (filteredHoods.every(hood => hood.stats.visits.length <= 0)) {
            message.error('No visits found for the selected dates.');
        } else {
            downloadNeighbourhoodsCsv(filteredHoods);
        }
    }), [actionHandler, filteredHoods, message]);

    useEffect(() => actionHandler.setHandler('exportNeighbourhoodEmployees', () => {
        downloadNeighbourhoodEmployeesCsv(filteredHoods);
    }), [actionHandler, filteredHoods]);

    return (
        <>
            <SeatingPlanStickyFilter>
                <NeighbourhoodListFilter filter={filter} setFilter={setFilter}/>
            </SeatingPlanStickyFilter>
            <VisitDataCoverageWarning
                dates={applyFilter(model.employeeVisits.dates, filterDateRange(filter.dateRange))}
                dateRange={filter.dateRange}
            />
            {filteredHoods.length === 0 ? (
                model.hoodItems.length === 0 ? (
                    <Alert sx={{my: 2}} color="info">Now new neighbourhoods should be created</Alert>
                ) : (
                    <Alert sx={{my: 2}} color="warning">No neighbourhoods match the current filters. Try adjusting your
                        filter criteria.</Alert>
                )
            ) : (
                <Virtuoso
                    className={PRINT_TARGET}
                    style={{marginTop: 16}}
                    data={filteredHoods}
                    useWindowScroll
                    computeItemKey={(_, hood) => hood.key}
                    itemContent={(_, hood) => (
                        <Box pb={2}>
                            <NeighbourhoodCard
                                neighbourhood={hood.neighbourhood}
                                highlight={hood.highlight}
                                onEdit={hood.onEdit}
                                onDelete={hood.onDelete}
                            >
                                <NeighbourhoodPaneEmployeeBlock
                                    stats={hood.stats}
                                    assignedDesks={model.assignedDesks}
                                    desks={hood.neighbourhood.newDesks}
                                />
                                <NeighbourhoodPaneStatBlock
                                    deskCount={hood.neighbourhood.newDesks.length}
                                    stats={hood.stats}
                                    assignedDesks={model.assignedDesks}
                                    statRanges={statRanges}
                                />
                                <NeighbourhoodPaneVisitChartBlock stats={hood.stats} deskCount={hood.neighbourhood.newDesks.length}/>
                            </NeighbourhoodCard>
                        </Box>
                    )}
                />
            )}
        </>
    );
}


export const neighbourhoodsTab: SeatingPlanTab<'exportNeighbourhoods' | 'exportNeighbourhoodEmployees' | 'newNeighbourhoodAction'> = {
    label: (model, active) => {
        const count = [];
        if (model.plan.newNeighbourhoods.length > 0) {
            count.push(`+${model.plan.newNeighbourhoods.length}`);
        }
        if (model.plan.neighbourhoodUpdates.length > 0) {
            count.push(`*${model.plan.neighbourhoodUpdates.length}`);
        }
        return (
            <SeatingPlanTabLabel label="Neighbourhoods" active={active} count={count.join(' ')}/>
        );
    },
    key: 'neighbourhoods',
    actions: {
        exportNeighbourhoods: exportCsvMenuItem({
            label: 'Export Neighbourhoods',
            tooltip: 'As CSV, respecting current filters',
        }),
        exportNeighbourhoodEmployees: exportCsvMenuItem({
            label: 'Export Desk Suggestions',
            tooltip: 'As CSV, respecting current filters',
        }),
        newNeighbourhoodAction,
    },
    content: NeighbourhoodListTab,
};
